import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import {
  LayoutDashboard, CalendarDays, Wallet, Receipt, TrendingDown, FileText,
  BarChart3, Users, School, DollarSign, UserCog, ScrollText, User,
  LogOut, Menu, X, GraduationCap, ClipboardList, NotebookPen, BookOpen, UserPlus,
} from "lucide-react";
import { Button } from "./ui/button";

const BM_NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/attendance", label: "Absensi & Teaching Record", icon: CalendarDays },
  { to: "/teaching-fee", label: "Teaching Fee", icon: Wallet },
  { to: "/income", label: "Penerimaan Sekolah", icon: Receipt },
  { to: "/expenses", label: "Pengeluaran GTS", icon: TrendingDown },
  { to: "/slips", label: "Slip Gaji", icon: FileText },
  { to: "/reports", label: "Laporan", icon: BarChart3 },
  { to: "/classes", label: "Manajemen Kelas", icon: BookOpen },
  { to: "/assignments", label: "Assignment Guru", icon: UserPlus },
  { to: "/teachers", label: "Data Guru dan Staf", icon: Users },
  { to: "/schools", label: "Data Sekolah", icon: School },
  { to: "/rates", label: "Pengaturan Tarif", icon: DollarSign },
  { to: "/users", label: "Manajemen Pengguna", icon: UserCog },
  { to: "/audit", label: "Audit Log", icon: ScrollText },
  { to: "/profile", label: "Profil", icon: User },
];

const COORD_NAV = [
  { to: "/dashboard", label: "Dashboard Kehadiran", icon: LayoutDashboard },
  { to: "/attendance", label: "Input Absensi", icon: CalendarDays },
  { to: "/recap", label: "Rekap Jumlah Sesi", icon: ClipboardList },
  { to: "/notes", label: "Catatan Mengajar", icon: NotebookPen },
  { to: "/classes", label: "Data Kelas", icon: BookOpen },
  { to: "/profile", label: "Profil", icon: User },
];

const TEACHER_NAV = [
  { to: "/teacher", label: "Dashboard", icon: LayoutDashboard },
  { to: "/classes", label: "Kelas Saya", icon: BookOpen },
  { to: "/profile", label: "Profil", icon: User },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [branding, setBranding] = useState({});

  const nav = user?.role === "branch_manager" ? BM_NAV : user?.role === "teacher" ? TEACHER_NAV : COORD_NAV;

  useEffect(() => {
    api.get("/settings/branding").then((r) => setBranding(r.data)).catch(() => {});
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-gtscream flex">
      {/* Sidebar */}
      <aside
        className={`fixed z-40 inset-y-0 left-0 w-72 bg-gtsnavy text-white flex flex-col transition-transform duration-300 lg:translate-x-0 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="sidebar"
      >
        <div className="flex items-center gap-3 px-6 py-6 border-b border-white/10">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-gtsorange to-gtsorange-light flex items-center justify-center shrink-0 overflow-hidden">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt="logo" className="w-full h-full object-cover" />
            ) : (
              <GraduationCap className="w-6 h-6 text-white" />
            )}
          </div>
          <div className="leading-tight">
            <p className="font-nunito font-extrabold text-lg">GTS IEC</p>
            <p className="text-[11px] text-white/60">Mustikajaya</p>
          </div>
          <button className="ml-auto lg:hidden" onClick={() => setOpen(false)} data-testid="sidebar-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto gts-scroll px-3 py-4 space-y-1">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setOpen(false)}
              data-testid={`nav-${item.to.replace("/", "")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-gradient-to-r from-gtsorange to-gtsorange-light text-white shadow-lg"
                    : "text-white/70 hover:bg-white/10 hover:text-white"
                }`
              }
            >
              <item.icon className="w-[18px] h-[18px] shrink-0" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="px-3 py-4 border-t border-white/10">
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium text-white/80 hover:bg-red-500/20 hover:text-white transition-colors"
          >
            <LogOut className="w-[18px] h-[18px]" />
            Logout
          </button>
        </div>
      </aside>

      {open && (
        <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setOpen(false)} />
      )}

      {/* Main */}
      <div className="flex-1 lg:ml-72 flex flex-col min-h-screen min-w-0">
        <header className="sticky top-0 z-20 backdrop-blur-xl bg-white/70 border-b border-orange-100 no-print">
          <div className="flex items-center gap-4 px-4 md:px-8 py-4">
            <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="sidebar-open">
              <Menu className="w-6 h-6 text-gtsnavy" />
            </button>
            <div className="min-w-0">
              <h1 className="font-nunito font-extrabold text-gtsnavy text-lg md:text-xl truncate">
                {branding.app_name || "GTS Attendance and Teaching Record"}
              </h1>
              <p className="text-[11px] md:text-xs text-muted-foreground truncate">
                {branding.subtitle || "Attendance, Teaching Record, and Monthly Fee Management"}
              </p>
            </div>
            <div className="ml-auto flex items-center gap-3">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-semibold text-gtsnavy leading-tight" data-testid="topbar-username">{user?.name}</p>
                <p className="text-[11px] text-gtsorange font-medium">
                  {user?.role === "branch_manager" ? "Branch Manager" : user?.role === "teacher" ? "Guru / Teacher" : "Coordinator GTS"}
                </p>
              </div>
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gtsorange to-gtsorange-light flex items-center justify-center text-white font-bold overflow-hidden">
                {user?.picture ? (
                  <img src={user.picture} alt="" className="w-full h-full object-cover" />
                ) : (
                  (user?.name || "?").charAt(0)
                )}
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 p-4 md:p-8 animate-fade-up overflow-x-hidden min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
